import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { AppProvider } from "./contexts/AppContext";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./contexts/AuthContext";
import { DevModeIndicator } from "./components/DevModeIndicator";
import Layout from "./components/Layout";
import Welcome from "./pages/Welcome";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import ProfileView from "./pages/ProfileView";
import ProfileEdit from "./pages/ProfileEdit";
import Updates from "./pages/Updates";
import NotFound from "./pages/NotFound";
import WorkingSurveyResults from "./pages/WorkingSurveyResults";
import DeclarativeModuleRouter from "./components/DeclarativeModuleRouter";
import ModuleProtectedRoute from "./components/ModuleProtectedRoute";
import ModuleErrorBoundary from "./components/ModuleErrorBoundary";
import { navigationEvents } from "./utils/navigationEvents";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Inner component that can use hooks
function AppContent() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Listen for navigation events from non-React contexts
  useEffect(() => {
    const unsubscribe = navigationEvents.onNavigate((detail) => {
      // First logout to clear auth state
      logout();
      // Then navigate
      navigate(detail.path, { 
        state: detail.state, 
        replace: detail.replace 
      });
    });

    return unsubscribe;
  }, [navigate, logout]);

  return (
    <div className="min-h-screen bg-background">
      <Toaster />
      <Sonner />
      <DevModeIndicator />
      <Layout>
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={
            <ModuleProtectedRoute>
              <Profile />
            </ModuleProtectedRoute>
          } />
          <Route path="/profile/view" element={
            <ModuleProtectedRoute>
              <ProfileView />
            </ModuleProtectedRoute>
          } />
          <Route path="/profile/edit" element={
            <ModuleProtectedRoute>
              <ProfileEdit />
            </ModuleProtectedRoute>
          } />
          <Route path="/module" element={
            <ModuleErrorBoundary fallbackPath="/updates">
              <DeclarativeModuleRouter />
            </ModuleErrorBoundary>
          } />
          <Route path="/updates" element={
            <ModuleProtectedRoute>
              <Updates />
            </ModuleProtectedRoute>
          } />
          <Route path="/survey/:surveyType/results" element={<WorkingSurveyResults />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </div>
  );
}

// Main App component
const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <AppContent />
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
